// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';

import { ALLOWED_PHOTO_CONTENT_TYPES, MAX_PHOTO_BYTES, MAX_SELECTION } from './constants';
import type { PickedPhoto } from './types';

export type PickOutcome =
  | { kind: 'picked'; photos: PickedPhoto[] }
  | { kind: 'cancelled' }
  | { kind: 'permission-denied'; source: 'camera' | 'library' }
  | { kind: 'rejected'; reason: 'too-large' | 'unsupported-type' };

/**
 * Camera capture and library picking, native only.
 *
 * On web `launchImageLibraryAsync` is a *silent* no-op — it resolves nothing and no dialog opens —
 * so the web surfaces use a hidden `<input type="file">` instead (see PhotoPickerBar). That input
 * also takes `capture="environment"`, which gets the mobile-browser camera for free; there is no
 * reason to rebuild the getUserMedia + canvas rig that config-course needs for its alignment
 * overlay.
 */
export const pickFromCamera = async (): Promise<PickOutcome> => {
  if (Platform.OS === 'web') {
    return { kind: 'cancelled' };
  }

  const permission = await ImagePicker.requestCameraPermissionsAsync();
  if (!permission.granted) {
    return { kind: 'permission-denied', source: 'camera' };
  }

  const result = await ImagePicker.launchCameraAsync({
    // quality 0.8 rather than 1: a 12MP capture is several MB before this, and the server downscales
    // to 1600px anyway, so the extra bytes only cost the player mobile data.
    quality: 0.8,
  });

  return await toOutcome(result);
};

export const pickFromLibrary = async (): Promise<PickOutcome> => {
  if (Platform.OS === 'web') {
    return { kind: 'cancelled' };
  }

  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!permission.granted) {
    return { kind: 'permission-denied', source: 'library' };
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    // Array form: MediaTypeOptions is deprecated in expo-image-picker 17.
    mediaTypes: ['images'],
    allowsMultipleSelection: true,
    selectionLimit: MAX_SELECTION,
    quality: 0.8,
  });

  return await toOutcome(result);
};

/** Web path: turn the files from a hidden `<input type="file">` into upload payloads. */
export const fromWebFiles = (files: File[]): PickOutcome => {
  const photos: PickedPhoto[] = [];

  for (const file of files.slice(0, MAX_SELECTION)) {
    if (file.size > MAX_PHOTO_BYTES) {
      return { kind: 'rejected', reason: 'too-large' };
    }

    photos.push({
      blob: file,
      contentType: normalizeContentType(file.type),
      byteSize: file.size,
      fileName: file.name || 'photo.jpg',
      previewUri: URL.createObjectURL(file),
    });
  }

  return photos.length > 0 ? { kind: 'picked', photos } : { kind: 'cancelled' };
};

const toOutcome = async (result: ImagePicker.ImagePickerResult): Promise<PickOutcome> => {
  if (result.canceled || result.assets.length === 0) {
    return { kind: 'cancelled' };
  }

  const photos: PickedPhoto[] = [];
  for (const asset of result.assets.slice(0, MAX_SELECTION)) {
    // expo-image-picker hands back a local URI; fetching it is how both this and the tour cover
    // upload turn it into a Blob the PUT can send.
    const response = await fetch(asset.uri);
    const blob = await response.blob();

    // Prefer the picker's own byte count. React Native's Blob does not always report a usable
    // size for a file:// fetch, and the BFF rejects a presign whose fileSize is 0 outright, which
    // surfaces as an upload that simply never happens.
    const byteSize = asset.fileSize && asset.fileSize > 0 ? asset.fileSize : blob.size;
    if (byteSize > MAX_PHOTO_BYTES) {
      return { kind: 'rejected', reason: 'too-large' };
    }

    const contentType = normalizeContentType(asset.mimeType ?? blob.type);

    photos.push({
      blob,
      contentType,
      byteSize,
      fileName: asset.fileName ?? inferFileName(contentType),
      previewUri: asset.uri,
    });
  }

  return { kind: 'picked', photos };
};

/**
 * Maps whatever the platform reports onto a MIME type the pipeline accepts, defaulting to JPEG.
 *
 * Deliberately never rejects. The working cover-image upload does `blob.type || 'image/jpeg'` and
 * accepts everything; an allow-list here was stricter than the picker is predictable — Android
 * camera captures report `image/jpg`, some providers report nothing, and one unexpected value meant
 * a silently dropped photo. HEIC is still kept out of the pipeline by the presign handler and by
 * asking the picker for JPEG, and the resize handler treats an undecodable image as a permanent
 * failure rather than retrying forever.
 */
const normalizeContentType = (raw: string | null | undefined): string => {
  const normalized = raw?.trim().toLowerCase();
  if (!normalized) {
    return 'image/jpeg';
  }

  // 'image/jpg' is not a real MIME type but plenty of Android providers emit it.
  if (normalized === 'image/jpg') {
    return 'image/jpeg';
  }

  return ALLOWED_PHOTO_CONTENT_TYPES.includes(normalized) ? normalized : 'image/jpeg';
};

const inferFileName = (contentType: string): string => {
  if (contentType === 'image/png') return 'photo.png';
  if (contentType === 'image/webp') return 'photo.webp';
  return 'photo.jpg';
};
